import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Logger } from 'src/providers/logger';
import { Events } from 'src/providers/events/events';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import * as Sentry from '@sentry/angular';

import { recordApiFailure } from 'src/shared/util/sentry-api-error';

/**
 * Set to false for a fire-and-forget request the customer never waits on, so a slow
 * reply doesn't send a Sentry warning.
 *
 * ```ts
 * http.post(url, body, { context: new HttpContext().set(REPORT_SLOW_REQUEST, false) })
 * ```
 */
export const REPORT_SLOW_REQUEST = new HttpContextToken<boolean>(() => true);

/**
 * Error Interceptor
 *
 * Centralized error handling for all HTTP requests:
 * - Logs errors via Logger service
 * - Handles auth token invalidation (401 errors)
 * - Sends Sentry warnings for slow requests (>6 seconds)
 * - Formats errors consistently
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly REQUEST_TAKING_LONG_THRESHOLD = 6000;

  constructor(
    private logger: Logger,
    private events: Events,
    private demoDataService: DemoDataService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const slowRequestTimer = req.context.get(REPORT_SLOW_REQUEST)
      ? setTimeout(() => this.onRequestTakingTooLong(req), this.REQUEST_TAKING_LONG_THRESHOLD)
      : undefined;

    return next.handle(req).pipe(
      finalize(() => {
        // Cancel the slow request timer when request completes (success or error)
        clearTimeout(slowRequestTimer);
      }),
      catchError((error: HttpErrorResponse) => {
        // Check for auth token errors
        this.checkAuthToken(error);

        // Log the error
        this.logger.requestError(error, req.url, req.body);

        // Format and re-throw the error
        const formatted = this.formatError(error);
        // So Sentry can name the request if no caller catches the error (CLIENT-APP-22J "Handled unknown error").
        if (error instanceof HttpErrorResponse) {
          recordApiFailure(formatted, { status: error.status, method: req.method, url: req.url });
        }
        return throwError(() => formatted);
      })
    );
  }

  private checkAuthToken(error: HttpErrorResponse): void {
    // Belt-and-braces: DemoInterceptor already guarantees every request resolves
    // with a 200 in preview mode, so a real 401 should never reach here. But if
    // one somehow does (e.g. a request that skipped the demo interceptor), it
    // must not log the preview session out — there's no real account to log into.
    if (this.demoDataService.isPreviewMode()) {
      return;
    }
    if (error instanceof HttpErrorResponse) {
      if (
        error?.error?.error?.message === 'The access token is invalid' ||
        error?.error?.error?.code === 401
      ) {
        this.events.publish('authToken:isInvalid');
      }
    }
  }

  private formatError(error: HttpErrorResponse): any {
    if (error?.error) {
      const apiError = error.error;
      if (apiError.error) {
        return {
          code: apiError.error.code,
          message: apiError.error.message,
          status: error.status,
          err: apiError
        };
      }
      if (this.isPlainObject(apiError)) {
        return { ...apiError, status: error.status };
      }
      return apiError;
    }
    return error;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  private onRequestTakingTooLong(req: HttpRequest<any>): void {
    const event: Sentry.Event = {
      message: `Request ${req.method} ${req.url} took longer than expected`,
      level: 'warning',
      extra: {
        method: req.method,
        url: req.url
      }
    };

    Sentry.captureEvent(event);
  }
}
